import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getConflict, listConflicts, reopenConflict, resolveConflict, type ConflictStatus } from './api';

export type ConflictTab = 'open' | 'resolved' | 'all';

export const conflictKeys = {
  all: ['conflicts'] as const,
  list: (tab: ConflictTab) => [...conflictKeys.all, 'list', tab] as const,
  one: (id: string) => [...conflictKeys.all, 'one', id] as const,
};

export function useConflicts(tab: ConflictTab) {
  return useQuery({ queryKey: conflictKeys.list(tab), queryFn: ({ signal }) => listConflicts(tab, signal) });
}

export function useConflict(id: string | undefined) {
  return useQuery({
    queryKey: conflictKeys.one(id ?? ''),
    queryFn: ({ signal }) => getConflict(id!, signal),
    enabled: !!id,
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: conflictKeys.all });
}

export function useResolveConflict() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { id: string; status: Exclude<ConflictStatus, 'open'>; resolvedValue?: string; note?: string }) =>
      resolveConflict(v.id, { status: v.status, resolvedValue: v.resolvedValue, note: v.note }),
    onSuccess: refresh,
  });
}

export function useReopenConflict() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (v: { id: string; note?: string }) => reopenConflict(v.id, v.note),
    onSuccess: refresh,
  });
}
